import { Button, FormControlLabel, Grid, Stack, Switch, Box } from "@mui/material";
import React, { useCallback } from "react";
import Modal, { CommonModalProps } from "..";
import { GetFormIsValid } from "../../../Redux/Reducers/redux-slice-form-validation";
import { produce } from "immer";
import { cacheService, dciService } from "../../../Core/Api/services";
import {
  DbKdmRecipientInfos,
  KdmRecipientDciServer,
  KdmRecipientProjectorBrand,
  KdmRecipientTheaterCompagny,
  KdmRecipientType,
} from "../../../Core/Api/Types/db-kdm-recipient-infos";
import { TranskodeVideoProfile } from "../../../Core/Api/Types/transkode-video-profile";
import { HdrType } from "../../../Core/Api/Types/hdr-type";
import FormSection from "../../Forms/FormSection/form-section";
import { ObjectType } from "../../../Core/Api/Types/object-type";
import UploadCard, { UploadCardHandle, UploadContext } from "../../Cards/UploadCard/upload-card";
import CardMembershipOutlinedIcon from "@mui/icons-material/CardMembershipOutlined";
import { readFileAsText } from "../../../Core/Utils/functions";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import FormInputText from "../../Forms/FormInputText/form-input-text";
import FormAutocomplete from "../../Forms/FormAutocomplete/form-autocomplete";
import { useAppSelector } from "../../../Redux/redux-store";
import CodeBox from "../../Misc/CodeBox/code-box";
import { useTranslation } from "react-i18next";

type CloseClbProps = {
  confirm: boolean;
};

export type ModalKdmRecipientElementProps = {
  recipient?: DbKdmRecipientInfos;
  recipients: DbKdmRecipientInfos[];
  setRecipients: React.Dispatch<React.SetStateAction<DbKdmRecipientInfos[]>>;
  onClose?: (props: CloseClbProps) => void;
};
export type ModalKdmRecipientProps = CommonModalProps<ModalKdmRecipientElementProps>;

const ModalKdmRecipient = React.forwardRef<any, ModalKdmRecipientProps>((props, ref) => {
  const { t } = useTranslation();
  const { closeModal, onClose, recipient, recipients, setRecipients, ...modalProps } = props;
  const [newRecipient, setNewRecipient] = React.useState<DbKdmRecipientInfos>(new DbKdmRecipientInfos({}));
  const uploadCert = React.useRef<UploadCardHandle>(null);
  const enqueueSnackbar = useCustomSnackbar();
  const formId = "GeneraleSectionFrom";
  const isValidForm = useAppSelector(GetFormIsValid(formId));
  const projectorFormId = "ProjectorSectionFrom";
  const isValidProjectorForm = useAppSelector(GetFormIsValid(projectorFormId));
  const contactFormId = "ContactSectionFrom";
  const isValidContactForm = useAppSelector(GetFormIsValid(contactFormId));

  const handleCancelClick = () => {
    onClose && onClose({ confirm: false });
    closeModal();
  };

  const transformFileToString = async (file: File[]) => {
    try {
      const fileContent = file.length === 0 ? null : await readFileAsText(file[0]);
      setNewRecipient(
        produce((draft) => {
          draft!.cert = fileContent;
        })
      );
    } catch (error) {
      console.error(error);
    }
  };

  React.useEffect(() => {
    if (recipient) {
      setNewRecipient(recipient);
    } else
      setNewRecipient(
        produce((draft) => {
          draft!.projector = {
            brand: KdmRecipientProjectorBrand.Unknown,
            dciServer: KdmRecipientDciServer.Unknown,
            hdrType: null,
            resolution: null,
            threeD: false,
          };
          draft!.platformId = cacheService.GetAppData()?.platformId || null;
          draft!.contact = { fullName: "", email: "", phone: "" };
        })
      );
  }, [recipient]);

  const handleConfirmClick = useCallback(() => {
    if (recipient) {
      dciService.UpdateKdmRecipient(newRecipient).then(function (response) {
        if (response.success) {
          const updatedArray: DbKdmRecipientInfos[] = recipients?.map((x) => {
            if (x.id === newRecipient.id) return newRecipient;
            else return x;
          });
          enqueueSnackbar(t("alert:success.kdmRecipientUpdated"), "success");
          setRecipients(updatedArray);
          onClose && onClose({ confirm: true });
          closeModal();
        } else enqueueSnackbar(response.message!, "error");
      });
    } else
      dciService.CreateKdmRecipient(newRecipient).then(function (response) {
        if (response.success) {
          setRecipients([...recipients, newRecipient]);
          enqueueSnackbar(t("alert:success.kdmRecipientCreated"), "success");
          onClose && onClose({ confirm: true });
          closeModal();
        } else enqueueSnackbar(response.message!, "error");
      });
  }, [recipient, newRecipient]);

  console.log("newRecipient", newRecipient.cert);

  return (
    <Modal
      {...modalProps}
      title={recipient ? t("modal:title.updateRecipient") : t("modal:title.newRecipient")}
      reference={ref}
      closeModal={handleCancelClick}
      width={680}
      footer={
        <Stack direction="row" justifyContent="space-between">
          <Button onClick={handleCancelClick}>{t("common:cancel")}</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleConfirmClick}
            disabled={!isValidForm || newRecipient.cert === null || !isValidProjectorForm}
          >
            {recipient ? t("common:update") : t("common:create")}
          </Button>
        </Stack>
      }
    >
      <FormSection
        title={t("common:generalInformation")}
        sx={{ mb: 3 }}
        status={isValidForm ? "complete" : "warning"}
        statusText={isValidForm ? t("common:configured") : t("common:configurationRequired")}
      >
        <Grid container spacing={2}>
          <Grid item md={6}>
            <FormInputText
              label={t("common:name")}
              fullWidth={true}
              value={newRecipient.name || ""}
              placeholder={t("common:lowerCaseName")}
              onChange={(e) => {
                setNewRecipient(
                  produce((draft) => {
                    draft!.name = e.target.value;
                  })
                );
              }}
              validationProps={{
                formId: formId,
                formSectionId: "name",
                fieldId: "name",
                required: true,
                initialState: true,
                initialValue: newRecipient.name || undefined,
              }}
            />
          </Grid>
          <Grid item md={6}>
            <FormInputText
              label={t("common:serialNumber")}
              fullWidth={true}
              value={newRecipient.serialNumber || ""}
              placeholder={t("common:serialNumber")}
              onChange={(e) => {
                setNewRecipient(
                  produce((draft) => {
                    draft!.serialNumber = e.target.value;
                  })
                );
              }}
              validationProps={{
                formId: formId,
                formSectionId: "Serial number",
                fieldId: "serial-number",
                required: false,
                initialState: true,
                initialValue: newRecipient.serialNumber || undefined,
              }}
            />
          </Grid>
          <Grid item md={6}>
            <FormInputText
              label={t("common:address")}
              fullWidth={true}
              value={newRecipient.address || ""}
              placeholder={t("common:lowerCaseAddress")}
              onChange={(e) => {
                setNewRecipient(
                  produce((draft) => {
                    draft!.address = e.target.value;
                  })
                );
              }}
              validationProps={{
                formId: formId,
                formSectionId: "address",
                fieldId: "address",
                required: true,
                initialState: true,
                initialValue: newRecipient.address || undefined,
              }}
            />
          </Grid>
          <Grid item md={6}>
            <FormInputText
              label={t("common:zipCode")}
              fullWidth={true}
              value={newRecipient.zipCode || ""}
              placeholder={t("common:lowerCaseZipCode")}
              onChange={(e) => {
                setNewRecipient(
                  produce((draft) => {
                    draft!.zipCode = e.target.value;
                  })
                );
              }}
              validationProps={{
                formId: formId,
                formSectionId: "zipCode",
                fieldId: "zipCode",
                required: true,
                initialState: true,
                initialValue: newRecipient.zipCode || undefined,
              }}
            />
          </Grid>
          <Grid item md={6}>
            <FormInputText
              label={t("common:city")}
              fullWidth={true}
              value={newRecipient.city || ""}
              placeholder={t("common:city")}
              onChange={(e) => {
                setNewRecipient(
                  produce((draft) => {
                    draft!.city = e.target.value;
                  })
                );
              }}
              validationProps={{
                formId: formId,
                formSectionId: "City",
                fieldId: "City",
                required: true,
                initialState: true,
                initialValue: newRecipient.city || undefined,
              }}
            />
          </Grid>
          <Grid item md={6}>
            <FormInputText
              label={t("common:country")}
              fullWidth={true}
              value={newRecipient.country || ""}
              placeholder={t("common:country")}
              onChange={(e) => {
                setNewRecipient(
                  produce((draft) => {
                    draft!.country = e.target.value;
                  })
                );
              }}
              validationProps={{
                formId: formId,
                formSectionId: "Country",
                fieldId: "Country",
                required: true,
                initialState: true,
                initialValue: newRecipient.country || undefined,
              }}
            />
          </Grid>
          <Grid item md={6}>
            <FormAutocomplete
              fullWidth
              options={Object.values(KdmRecipientTheaterCompagny)}
              label={t("field:label.theaterCompany")}
              onChange={(e) =>
                setNewRecipient(
                  produce((draft) => {
                    if (draft) draft!.theater = e;
                  })
                )
              }
              value={newRecipient.theater}
              validationProps={{
                formId: formId,
                formSectionId: "TheaterCompany",
                fieldId: "TheaterCompany",
                required: true,
                initialState: false,
              }}
            />
          </Grid>
          <Grid item md={6}>
            <FormAutocomplete
              fullWidth
              options={Object.values(KdmRecipientType).filter((x) => x !== KdmRecipientType.KdmGenServer)}
              label={t("field:label.recipientType")}
              onChange={(e) =>
                setNewRecipient(
                  produce((draft) => {
                    if (draft) draft!.type = e;
                  })
                )
              }
              value={newRecipient.type}
              validationProps={{
                formId: formId,
                formSectionId: "RecipientType",
                fieldId: "RecipientType",
                required: true,
                initialState: false,
              }}
            />
          </Grid>
          <Grid item md={6}>
            <FormInputText
              label={t("common:roomName")}
              fullWidth={true}
              value={newRecipient.roomName || ""}
              placeholder={t("common:roomName")}
              onChange={(e) => {
                setNewRecipient(
                  produce((draft) => {
                    draft!.roomName = e.target.value;
                  })
                );
              }}
              validationProps={{
                formId: formId,
                formSectionId: "roomName",
                fieldId: "roomName",
                required: false,
                initialState: false,
              }}
            />
          </Grid>
        </Grid>
      </FormSection>
      <FormSection
        title={t("form:section.title.cinemaProjector")}
        sx={{ mb: 3 }}
        status={isValidProjectorForm ? "complete" : "warning"}
        statusText={isValidProjectorForm ? t("common:configured") : t("common:configurationRequired")}
      >
        <Grid container spacing={2}>
          <Grid item md={6}>
            <FormAutocomplete
              fullWidth
              options={Object.values(KdmRecipientProjectorBrand)}
              label={t("field:label.projectorBrand")}
              onChange={(e) =>
                setNewRecipient(
                  produce((draft) => {
                    if (draft) draft!.projector!.brand = e;
                  })
                )
              }
              value={newRecipient.projector?.brand}
              validationProps={{
                formId: projectorFormId,
                formSectionId: "ProjectorBrand",
                fieldId: "ProjectorBrand",
                required: true,
                initialState: false,
              }}
            />
          </Grid>
          <Grid item md={6}>
            <FormAutocomplete
              fullWidth
              options={Object.values(KdmRecipientDciServer)}
              label={t("field:label.dciServer")}
              onChange={(e) =>
                setNewRecipient(
                  produce((draft) => {
                    if (draft) draft!.projector!.dciServer = e;
                  })
                )
              }
              value={newRecipient.projector?.dciServer}
              validationProps={{
                formId: projectorFormId,
                formSectionId: "DCIServer",
                fieldId: "DCIServer",
                required: true,
                initialState: false,
              }}
            />
          </Grid>
          <Grid item md={6}>
            <FormAutocomplete
              fullWidth
              options={Object.values(TranskodeVideoProfile)}
              label={t("common:resolution")}
              onChange={(e) =>
                setNewRecipient(
                  produce((draft) => {
                    if (draft) draft!.projector!.resolution = e;
                  })
                )
              }
              value={newRecipient.projector?.resolution}
              validationProps={{
                formId: projectorFormId,
                formSectionId: "ResolutionProjcetor",
                fieldId: "ResolutionProjcetor",
                required: true,
                initialState: false,
              }}
            />
          </Grid>
          <Grid item md={6}>
            <FormAutocomplete
              fullWidth
              options={Object.values(HdrType)}
              label={t("field:label.hdrtype")}
              onChange={(e) =>
                setNewRecipient(
                  produce((draft) => {
                    if (draft) draft!.projector!.hdrType = e;
                  })
                )
              }
              value={newRecipient.projector?.hdrType}
              validationProps={{
                formId: projectorFormId,
                formSectionId: "hdrType",
                fieldId: "hdrType",
                required: false,
                initialState: false,
              }}
            />
          </Grid>
          <Grid item md={6}>
            <FormControlLabel
              sx={{}}
              control={
                <Switch
                  size="small"
                  checked={newRecipient.projector?.threeD || false}
                  onChange={(e) =>
                    setNewRecipient(
                      produce((draft) => {
                        draft!.projector!.threeD = e.target.checked;
                      })
                    )
                  }
                />
              }
              label="3D"
            />
          </Grid>
        </Grid>
      </FormSection>
      <FormSection
        title={t("common:contact")}
        sx={{ mb: 3 }}
        status={isValidContactForm ? "complete" : "warning"}
        statusText={isValidContactForm ? t("common:configured") : t("common:configurationRequired")}
      >
        <Grid container spacing={2}>
          <Grid item md={6}>
            <FormInputText
              label={t("common:contactName")}
              fullWidth={true}
              value={newRecipient.contact?.fullName || ""}
              placeholder={t("common:contactName")}
              onChange={(e) => {
                setNewRecipient(
                  produce((draft) => {
                    if (!draft!.contact) draft!.contact = { fullName: "", email: "", phone: "" };
                    draft!.contact.fullName = e.target.value;
                  })
                );
              }}
              validationProps={{
                formId: contactFormId,
                formSectionId: "ContactfullName",
                fieldId: "ContactfullName",
                required: true,
                initialState: false,
              }}
            />
          </Grid>
          <Grid item md={6}>
            <FormInputText
              label={t("common:contactPhone")}
              fullWidth={true}
              value={newRecipient.contact?.phone || ""}
              placeholder={t("common:contactPhone")}
              onChange={(e) => {
                setNewRecipient(
                  produce((draft) => {
                    if (!draft!.contact) draft!.contact = { fullName: "", email: "", phone: "" };
                    draft!.contact.phone = e.target.value;
                  })
                );
              }}
              validationProps={{
                formId: contactFormId,
                formSectionId: "Contactphone",
                fieldId: "Contactphone",
                required: false,
                initialState: false,
              }}
            />
          </Grid>
          <Grid item md={12}>
            <FormInputText
              label={t("common:contactEmail")}
              fullWidth={true}
              value={newRecipient.contact?.email || ""}
              placeholder={t("common:contactEmail")}
              onChange={(e) => {
                setNewRecipient(
                  produce((draft) => {
                    if (!draft!.contact) draft!.contact = { fullName: "", email: "", phone: "" };
                    draft!.contact.email = e.target.value;
                  })
                );
              }}
              validationProps={{
                formId: contactFormId,
                formSectionId: "Contactemail",
                fieldId: "Contactemail",
                required: true,
                initialState: false,
              }}
            />
          </Grid>
        </Grid>
      </FormSection>
      <Box>
        <UploadCard
          title={t("button:uploadCertificate")}
          ref={uploadCert}
          coverPlaceholderIcon={<CardMembershipOutlinedIcon />}
          userHasRightToDelete={true}
          uploadButtonInCard={false}
          context={UploadContext.KdmCert}
          onDroppedFiles={transformFileToString}
          clear={() => {
            setNewRecipient(
              produce((draft) => {
                draft!.cert = null;
              })
            );
          }}
          objectId={0}
          format={{
            id: 1,
            isUploaded: false,
            maxFileSizeMb: 10,
            name: "File",
            userHasRighToDelete: false,
            acceptsFiles: { ["application/octet-stream"]: [".pem"] },
            description: t("common:certificate"),
            isUserDataMandatory: false,
            objectType: ObjectType.Others,
          }}
        />
      </Box>
      {recipient && recipient.cert && (
        <FormSection title={t("form:section.title.certificateViewer")} sx={{ mt: 3 }} defaultExpanded={false}>
          <Stack spacing={1}>
            <CodeBox text={recipient.cert} xml={true} />
          </Stack>
        </FormSection>
      )}
    </Modal>
  );
});

export default ModalKdmRecipient;
